import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, MapPin, Briefcase, Heart, Zap, Users } from 'lucide-react';
import Footer from "../../../components/landing/Footer";
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";

const values = [
  { icon: Heart, title: "Purpose-Driven", desc: "We're making quality housing accessible to every Indian." },
  { icon: Zap, title: "Move Fast", desc: "We ship quickly, learn from users, and iterate." },
  { icon: Users, title: "People First", desc: "Hosts, guests, and team members all matter equally." },
  { icon: Briefcase, title: "Ownership", desc: "Everyone owns their work from idea to impact." },
];

const openings = [
  { title: "Full-Stack Engineer", team: "Engineering", location: "Hyderabad (Hybrid)", type: "Full-time" },
  { title: "Product Designer (UI/UX)", team: "Design", location: "Hyderabad (Hybrid)", type: "Full-time" },
  { title: "Growth & Marketing Manager", team: "Marketing", location: "Hyderabad", type: "Full-time" },
  { title: "City Operations Manager", team: "Operations", location: "Multiple Cities", type: "Full-time" },
  { title: "Customer Success Associate", team: "Support", location: "Remote", type: "Full-time" },
];

const Careers: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors">
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
              <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Sign Up</Button></Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="block text-foreground/80 hover:text-primary font-medium transition-colors py-2">
                  {item}
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Hero */}
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-foreground mb-4">Join the Social Bunkr Team</h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                We're building Bharat's most affordable stay platform. Come help us make quality housing accessible to millions.
              </p>
            </div>

            {/* Values */}
            <h2 className="text-2xl font-bold text-foreground mb-6">Why Work With Us</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-16">
              {values.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-background rounded-2xl shadow-sm p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                    <p className="text-foreground/70 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Openings */}
            <h2 className="text-2xl font-bold text-foreground mb-6">Open Positions</h2>
            <div className="space-y-4 mb-12">
              {openings.map((job) => (
                <div key={job.title} className="bg-background rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{job.title}</h3>
                    <div className="flex flex-wrap gap-3 mt-2">
                      <span className="text-sm text-muted-foreground">{job.team}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {job.location}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-sm text-muted-foreground">{job.type}</span>
                    </div>
                  </div>
                  <Link to="/contact">
                    <Button variant="outline" className="rounded-full px-6 whitespace-nowrap">Apply Now</Button>
                  </Link>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-background rounded-2xl shadow-sm p-8 text-center">
              <h2 className="text-xl font-semibold text-foreground mb-2">Don't see your role?</h2>
              <p className="text-muted-foreground mb-6">
                We're always looking for talented people. Send us your resume and tell us how you'd like to contribute.
              </p>
              <a href="mailto:hello@socialbunkr.in">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Send Your Resume
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
